import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Breeder } from 'shared/@types';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falLocationDot } from 'shared/@generated/icons/falLocationDot';
import { falPaw } from 'shared/@generated/icons/falPaw';
import { falTag } from 'shared/@generated/icons/falTag';
import { falTimes } from 'shared/@generated/icons/falTimes';
import { Button } from 'shared/components/button';
import { Icon, type FaIcon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import LikeButton from 'shared/components/like-button';
import { Loader } from 'shared/components/loader';
import { Text } from 'shared/components/text';
import { getBreederImageAsset } from 'shared/library/get-breeder-image';

function SelectedBreederMetaRow({ icon, children }: { icon: FaIcon; children: React.ReactNode }) {
  return (
    <View className={'flex-row items-center'}>
      <Icon
        icon={icon}
        className={
          'text-brand-brown-dark/60 mr-1.5 size-3.5 w-3.5 shrink-0 md:mr-2 md:size-4 md:w-4'
        }
      />
      <Text className={'text-brand-brown-dark/70 flex-1 text-sm font-semibold'}>{children}</Text>
    </View>
  );
}

export function BreederMapCard({
  breeder,
  activeListingsCount,
  onClose,
}: {
  breeder: Breeder | null;
  activeListingsCount: number;
  onClose: () => void;
}) {
  const { t } = useTranslation('screens');
  const owner = breeder?.owners?.[0] ?? breeder?.users?.[0] ?? null;
  const breedNames = breeder?.breeds.map((breed) => breed.name).join(', ') ?? '';
  const description =
    breeder?.description?.trim() || t('screens:breeders.show_breeder.no_description');

  return (
    <View
      testID={'breeder-map-card'}
      className={
        'shadow-brand-brown-dark/20 w-full rounded-lg bg-white p-2.5 shadow-xl md:max-h-[calc(100svh-var(--header-h)-4rem)] md:w-80 md:overflow-y-auto md:p-3'
      }
    >
      {breeder ? (
        <View>
          <View className={'absolute left-0 top-0 z-10 p-1.5'}>
            <LikeButton
              modelId={breeder.id}
              modelType={'App\\Models\\Breeder'}
              className={'p-2 md:p-3'}
            />
          </View>

          <View className={'absolute right-0 top-0 z-10 p-1.5'}>
            <TouchableOpacity
              testID={'breeder-map-card-close'}
              accessibilityLabel={t('common:close')}
              className={'items-center justify-center rounded-full bg-white p-1.5 hover:opacity-75'}
              onPress={onClose}
            >
              <Icon icon={falTimes} className={'size-4'} />
            </TouchableOpacity>
          </View>

          <Image
            src={getBreederImageAsset(breeder)}
            alt={breeder.name}
            containerClassName={'md:aspect-4/3 aspect-video w-full'}
            imageClassName={'rounded-lg object-cover'}
            sizeType={'card'}
          />

          <View className={'mt-3'}>
            <Text className={'text-brand-brown-dark text-base font-bold md:text-lg'}>
              {breeder.name}
            </Text>

            <View className={'mt-2 gap-1.5'}>
              {owner?.city && (
                <SelectedBreederMetaRow icon={falLocationDot}>{owner.city}</SelectedBreederMetaRow>
              )}

              <SelectedBreederMetaRow icon={falTag}>
                {t('screens:breeders.index_breeders_map.active_listings_count', {
                  count: activeListingsCount,
                })}
              </SelectedBreederMetaRow>

              {breedNames && (
                <SelectedBreederMetaRow icon={falPaw}>{breedNames}</SelectedBreederMetaRow>
              )}
            </View>

            <Text
              className={'text-brand-brown-dark mt-3 text-sm md:mt-4 md:text-base'}
              numberOfLines={3}
            >
              {description}
            </Text>

            <Button
              href={`/@${breeder.slug}`}
              text={t('screens:breeders.index_breeders_map.view_profile')}
              icon={falAngleRight}
              color={'brown'}
              dense={true}
              className={'mt-3 md:mt-4'}
            />
          </View>
        </View>
      ) : (
        <Loader />
      )}
    </View>
  );
}
